import React, { forwardRef, useState } from 'react';
import CustomModal from './component/CustomModal';
import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    ListItemIcon,
    Paper
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import StorageIcon from '@mui/icons-material/Storage';
import SettingsIcon from '@mui/icons-material/Settings';
import UploadFile from './files/Files';
import Database from './database/Database';
import Config from './Config';

// Constants for section types
const SECTION_TYPES = {
    FILES: 'files',
    DATABASE: 'database',
    CONFIG: 'config'
};

const PrivateKnowledgeBaseModal = forwardRef(({ handleClose }, ref) => {
    const [activeSection, setActiveSection] = useState(SECTION_TYPES.FILES);

    // Handle section change
    const handleSectionChange = (section) => {
        setActiveSection(section);
    };

    // Render sidebar
    const renderSidebar = () => (
        <Paper
            sx={{
                width: '250px',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '8px 0 0 8px',
                overflow: 'hidden'
            }}
        >
            <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
                <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: '#333' }}
                >
                    Knowledge Base
                </Typography>
            </Box>

            <List sx={{ p: 0 }}>
                <ListItem disablePadding>
                    <ListItemButton
                        selected={activeSection === SECTION_TYPES.FILES}
                        onClick={() => handleSectionChange(SECTION_TYPES.FILES)}
                        sx={{
                            py: 2,
                            '&.Mui-selected': {
                                backgroundColor: '#e3f2fd',
                                borderRight: '3px solid #1976d2'
                            }
                        }}
                    >
                        <ListItemIcon>
                            <FolderIcon
                                color={
                                    activeSection === SECTION_TYPES.FILES
                                        ? 'primary'
                                        : 'inherit'
                                }
                            />
                        </ListItemIcon>
                        <ListItemText
                            primary="Files"
                            primaryTypographyProps={{
                                fontWeight:
                                    activeSection === SECTION_TYPES.FILES
                                        ? 600
                                        : 400
                            }}
                        />
                    </ListItemButton>
                </ListItem>

                <ListItem disablePadding>
                    <ListItemButton
                        selected={activeSection === SECTION_TYPES.DATABASE}
                        onClick={() =>
                            handleSectionChange(SECTION_TYPES.DATABASE)
                        }
                        sx={{
                            py: 2,
                            '&.Mui-selected': {
                                backgroundColor: '#e3f2fd',
                                borderRight: '3px solid #1976d2'
                            }
                        }}
                    >
                        <ListItemIcon>
                            <StorageIcon
                                color={
                                    activeSection === SECTION_TYPES.DATABASE
                                        ? 'primary'
                                        : 'inherit'
                                }
                            />
                        </ListItemIcon>
                        <ListItemText
                            primary="Database"
                            primaryTypographyProps={{
                                fontWeight:
                                    activeSection === SECTION_TYPES.DATABASE
                                        ? 600
                                        : 400
                            }}
                        />
                    </ListItemButton>
                </ListItem>

                <ListItem disablePadding>
                    <ListItemButton
                        selected={activeSection === SECTION_TYPES.CONFIG}
                        onClick={() =>
                            handleSectionChange(SECTION_TYPES.CONFIG)
                        }
                        sx={{
                            py: 2,
                            '&.Mui-selected': {
                                backgroundColor: '#e3f2fd',
                                borderRight: '3px solid #1976d2'
                            }
                        }}
                    >
                        <ListItemIcon>
                            <SettingsIcon
                                color={
                                    activeSection === SECTION_TYPES.CONFIG
                                        ? 'primary'
                                        : 'inherit'
                                }
                            />
                        </ListItemIcon>
                        <ListItemText
                            primary="Configuration"
                            primaryTypographyProps={{
                                fontWeight:
                                    activeSection === SECTION_TYPES.CONFIG
                                        ? 600
                                        : 400
                            }}
                        />
                    </ListItemButton>
                </ListItem>
            </List>
        </Paper>
    );

    const handleSubmit = () => {
        // Handle submit logic here
        console.log('Saving knowledge base configuration...');
        handleClose();
    };

    // Render content based on active section
    const renderContent = () => {
        switch (activeSection) {
            case SECTION_TYPES.FILES:
                return <UploadFile />;
            case SECTION_TYPES.DATABASE:
                return <Database />;
            case SECTION_TYPES.CONFIG:
                return <Config />;
            default:
                return <UploadFile />;
        }
    };

    return (
        <CustomModal
            handleClose={handleClose}
            handleSubmit={handleSubmit}
            modalTitle="Private Knowledge Base"
            buttonText="Save Configuration"
            ref={ref}
            maxWidth="lg"
            fullWidth
        >
            <Box
                sx={{
                    display: 'flex',
                    height: '600px',
                    overflow: 'hidden'
                }}
            >
                {/* Sidebar */}
                {renderSidebar()}

                {/* Main Content */}
                <Box
                    sx={{
                        flex: 1,
                        backgroundColor: 'white',
                        borderRadius: '0 8px 8px 0',
                        overflow: 'hidden'
                    }}
                >
                    {renderContent()}
                </Box>
            </Box>
        </CustomModal>
    );
});

export default PrivateKnowledgeBaseModal;
